<template>
  <v-app id="app_main_navigation_drawer">
    <v-app-bar style="border-radius: 0px !important;" class="custom_card_box" border="sm opacity-1">
      <v-app-bar-nav-icon @click="drawer = !drawer"></v-app-bar-nav-icon>

      <v-app-bar-title class="display_only_mobile_appBar">
        <v-img class="custom_company_logo_nav ml-4" :src="require('@/img/company_logo/logo-dark 2.png')"></v-img>
      </v-app-bar-title>

      <v-app-bar-title class="display_only_web_appBar">
        <div class="d-flex align-center" style="height: 100%;">
          <v-divider :thickness="3" vertical class="mx-3 my-0"></v-divider>

          <!-- First Logo - fixed vertical alignment -->
          <img
            class="custom_company_logo_nav"
            style="display: block; max-height: 40px; width: auto; object-fit: contain;"
            :src="require('@/img/company_logo/logo-dark 2.png')"
            alt="Company Logo"
          />

          <!-- Second Logo - fixed vertical alignment and spacing -->
          <img
            class="custom_company_logo_nav ml-3"
            style="display: block; max-height: 40px; width: auto; object-fit: contain;"
            :src="require('@/img/company_logo/g-sfa.png')"
            alt="Second Logo"
          />
        </div>
      </v-app-bar-title>

      <v-spacer></v-spacer>

      <!-- Notifications Menu -->
      <v-menu v-model="notificationDialog" :close-on-content-click="false" offset-y left transition="none"
        :close-on-back="false" eager>
        <template v-slot:activator="{ props }">
          <v-btn class="mr-4" icon v-bind="props" variant="text">
            <v-badge :model-value="unreadCount > 0" color="error" :content="unreadCount > 99 ? '99+' : unreadCount"
              overlap>
              <v-icon size="24">mdi-bell-outline</v-icon>
            </v-badge>
          </v-btn>
        </template>

        <v-card class="notification-popup" style="width: 380px; max-width: 90vw;">
          <div class="notification-header pa-3 d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <v-icon size="20" color="primary" class="mr-2">mdi-bell</v-icon>
              <span class="text-subtitle-1 font-weight-medium">Notifications</span>
              <v-chip v-if="unreadCount > 0" size="x-small" color="error" class="ml-2">
                {{ unreadCount }} new
              </v-chip>
            </div>
            <v-btn v-if="unreadCount > 0" variant="text" size="small" color="primary" :loading="markingAllRead"
              @click="markAllAsRead">
              Mark all read
            </v-btn>
          </div>

          <v-divider></v-divider>

          <div class="notification-list" style="max-height: 400px; overflow-y: auto">
            <div v-if="loading && notifications.length === 0" class="text-center pa-6">
              <v-progress-circular indeterminate size="32" color="primary"></v-progress-circular>
              <div class="mt-2 text-caption text-grey">Loading notifications...</div>
            </div>

            <div v-else-if="sortedNotifications.length === 0" class="text-center pa-6">
              <v-icon size="48" color="grey-lighten-1" class="mb-2">mdi-bell-off-outline</v-icon>
              <div class="text-subtitle-2 text-grey">No notifications</div>
              <div class="text-caption text-grey-lighten-1">You're all caught up!</div>
            </div>

            <template v-else>
              <div v-for="notification in sortedNotifications" :key="notification.id"
                :class="['notification-item-wrapper', { 'unread-item-wrapper': !notification.is_read }]"
                @click="openNotificationDetails(notification)">
                <div class="notification-item pa-3">
                  <div class="d-flex">
                    <div class="flex-shrink-0 mr-3">
                      <div class="notification-icon"
                        :style="{ backgroundColor: getNotificationColor(notification.type) }">
                        <v-icon size="18" color="white">{{ getNotificationIcon(notification.type) }}</v-icon>
                      </div>
                    </div>
                    <div class="flex-grow-1">
                      <div class="d-flex justify-space-between align-start">
                        <div class="notification-title font-weight-medium text-body-2">
                          {{ notification.title }}
                          <span v-if="!notification.is_read" class="unread-dot ml-1"></span>
                        </div>
                        <div class="text-caption text-grey-lighten-1 ml-2" style="white-space: nowrap;">
                          {{ formatDate(notification.created_at) }}
                        </div>
                      </div>
                      <div class="notification-body text-caption text-grey-darken-1 mt-1">
                        {{ notification.body }}
                      </div>
                    </div>
                  </div>
                </div>
                <v-divider
                  v-if="sortedNotifications.indexOf(notification) < sortedNotifications.length - 1"></v-divider>
              </div>
            </template>
          </div>

          <div v-if="hasMorePages" class="notification-footer pa-2 text-center">
            <v-btn variant="text" size="small" color="primary" :loading="loadingMore" @click="loadMoreNotifications"
              block>
              Load more notifications
            </v-btn>
          </div>
        </v-card>
      </v-menu>

      <!-- Profile Menu -->
      <v-menu v-model="menu" :close-on-content-click="false" offset-y left transition="none">
        <template v-slot:activator="{ props }">
          <v-btn class="mr-4" icon v-bind="props" variant="text">
            <v-avatar size="36">
              <v-img :src="getProfileImageUrl()" class="avatar-image" @error="handleProfileImageError" />
            </v-avatar>
          </v-btn>
        </template>

        <v-card class="mx-auto custom_card_box" elevation="3" border="sm opacity-1" rounded="lg" min-width="300">
          <v-list class="bg-transparent">
            <v-list-item class="pa-4" :prepend-avatar="getProfileImageUrl()">
              <v-list-item-title class="font-weight-bold text-subtitle-1">{{ userDetials.name || 'Loading...'
              }}</v-list-item-title>
              <v-list-item-subtitle class="text-caption">{{ userDetials.email || 'Please wait' }}</v-list-item-subtitle>

              <template v-slot:append>
                <v-chip size="x-small" color="#BB0000" variant="tonal" class="text-uppercase font-weight-bold">
                  {{ userDetials.user_type || 'User' }}
                </v-chip>
              </template>
            </v-list-item>
          </v-list>

          <v-divider></v-divider>

          <v-card-actions class="pa-4">
            <v-btn block prepend-icon="mdi-logout" variant="flat" style="background-color: #8E44AD; color: white;"
              @click="isLogOut()">
              SIGN OUT
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-app-bar>

    <!-- Navigation Drawer -->
    <v-navigation-drawer style="border: none !important;" v-model="drawer" app clipped>
      <v-card border="sm opacity-1" class="mx-2 mt-3 drawer-content" elevation="1" rounded="lg">
        <v-list background dense density="compact" nav rounded>
          <template v-for="(module, index) in visibleNavModules" :key="index">
            <v-list-item v-if="module.type === 'single'" :to="module.to" active-class="red-background" link
              class="custom_list_group">
              <template v-slot:prepend>
                <v-icon size="small">{{ module.icon }}</v-icon>
              </template>
              <v-list-item-title class="custom_active font-weight-medium text-body-1">{{ module.title
              }}</v-list-item-title>
            </v-list-item>

            <template v-else-if="module.type === 'group'">
              <v-list-item :class="{ 'red-background': activeSubMenu === module.key }" link
                @click="toggleSubMenu(module.key)" class="custom_list_group">
                <template v-slot:prepend>
                  <v-icon size="small">{{ module.icon }}</v-icon>
                </template>
                <v-list-item-title class="custom_active font-weight-medium text-body-1">{{ module.title
                }}</v-list-item-title>
                <template v-slot:append>
                  <v-icon size="small">{{ activeSubMenu === module.key ? 'mdi-chevron-up' : 'mdi-chevron-down'
                  }}</v-icon>
                </template>
              </v-list-item>

              <v-expand-transition>
                <div v-show="activeSubMenu === module.key" class="ml-4 pl-2 sub-menu-wrapper">
                  <template v-for="(child, cIndex) in module.children" :key="cIndex">
                    <v-list-item class="font-weight-medium text-body-2 custom_list_group" :to="child.to"
                      color="#BB0000">
                      <template v-slot:prepend>
                        <v-icon size="small">mdi-menu-right</v-icon>
                      </template>
                      <v-list-item-title class="custom_active text-body-2">{{ child.title }}</v-list-item-title>
                    </v-list-item>
                  </template>
                </div>
              </v-expand-transition>
            </template>
          </template>
        </v-list>

        <v-card-title class="text-center">
          <v-divider></v-divider>
        </v-card-title>
      </v-card>
    </v-navigation-drawer>

    <v-main :class="{ 'drawer-expanded': drawer }">
      <router-view />
    </v-main>

    <!-- Centered Notification Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="500px" transition="dialog-transition" persistent>
      <v-card class="notification-details-dialog">
        <v-card-title class="pa-4 d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <div class="dialog-icon mr-3"
              :style="{ backgroundColor: selectedNotification ? getNotificationColor(selectedNotification.type) : '#9E9E9E' }">
              <v-icon size="24" color="white">{{ selectedNotification ? getNotificationIcon(selectedNotification.type) :
                'mdi-bell' }}</v-icon>
            </div>
            <span class="text-h6 font-weight-bold">{{ selectedNotification?.title || 'Notification Details' }}</span>
          </div>
          <v-btn icon variant="text" @click="closeDetailsDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-4">
          <div class="details-content">
            <div class="detail-section mb-4">
              <div class="detail-label text-caption font-weight-bold text-grey-darken-1 mb-1">
                <v-icon size="14" class="mr-1">mdi-message-text</v-icon>
                MESSAGE
              </div>
              <div class="detail-value text-body-1">
                {{ selectedNotification?.body }}
              </div>
            </div>

            <div v-if="selectedNotification?.meta" class="detail-section mb-4">
              <div class="detail-label text-caption font-weight-bold text-grey-darken-1 mb-2">
                <v-icon size="14" class="mr-1">mdi-information</v-icon>
                DETAILS
              </div>
              <div class="details-grid">
                <div v-for="(value, key) in selectedNotification.meta" :key="key" class="detail-row mb-2">
                  <div class="detail-key text-caption font-weight-medium text-grey-darken-1">
                    {{ formatKey(key) }}:
                  </div>
                  <div class="detail-value text-body-2">
                    {{ value }}
                  </div>
                </div>
              </div>
            </div>

            <div class="detail-section mb-4">
              <div class="detail-label text-caption font-weight-bold text-grey-darken-1 mb-1">
                <v-icon size="14" class="mr-1">mdi-tag</v-icon>
                TYPE
              </div>
              <div class="detail-value">
                <v-chip size="small" :color="getNotificationColor(selectedNotification?.type)" text-color="white">
                  {{ formatType(selectedNotification?.type) }}
                </v-chip>
              </div>
            </div>

            <div class="detail-section mb-4">
              <div class="detail-label text-caption font-weight-bold text-grey-darken-1 mb-1">
                <v-icon size="14" class="mr-1">mdi-calendar-clock</v-icon>
                DATE & TIME
              </div>
              <div class="detail-value text-body-2">
                {{ selectedNotification ? formatFullDate(selectedNotification.created_at) : '' }}
              </div>
            </div>

            <div class="detail-section">
              <div class="detail-label text-caption font-weight-bold text-grey-darken-1 mb-1">
                <v-icon size="14" class="mr-1">mdi-check-circle</v-icon>
                STATUS
              </div>
              <div class="detail-value">
                <v-chip size="small" :color="selectedNotification?.is_read ? 'success' : 'warning'" variant="tonal">
                  {{ selectedNotification?.is_read ? 'Read' : 'Unread' }}
                </v-chip>
              </div>
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn variant="text" color="grey" @click="closeDetailsDialog">Close</v-btn>
          <v-btn v-if="selectedNotification && !selectedNotification.is_read" variant="flat" color="primary"
            @click="markAsReadAndClose">
            Mark as Read
          </v-btn>
          <v-btn v-if="selectedNotification?.entity_type === 'lead' && selectedNotification?.entity_id" variant="flat"
            color="primary" @click="navigateToEntity">
            View {{ capitalize(selectedNotification.entity_type) }}
          </v-btn>
          <v-btn v-if="selectedNotification?.entity_type === 'route' && selectedNotification?.entity_id" variant="flat"
            color="primary" @click="navigateToEntity">
            View {{ capitalize(selectedNotification.entity_type) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import { mapState } from "vuex";
import axios from 'axios';
import { canAccessRoute } from "@/plugins/permissions";

const DEFAULT_AVATAR = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNlMGVlZjAiLz48Y2lyY2xlIGN4PSI1MCIgY3k9IjQwIiByPSIxNSIgZmlsbD0iIzkwYThhZCIvPjxwYXRoIGQ9Ik0yMCA3MCBMMjAgNjAgQzIwIDUwIDQwIDUwIDUwIDUwIEM2MCA1MCA4MCA1MCA4MCA2MCBMODAgNzAiIGZpbGw9IiM5MGE4YWQiLz48L3N2Zz4=';

const suppressResizeObserverErrors = () => {
  const resizeObserverErrHandler = (e) => {
    if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
      e.message === 'ResizeObserver loop limit exceeded') {
      e.stopImmediatePropagation();
      return true;
    }
  };
  window.addEventListener('error', resizeObserverErrHandler);
  return () => window.removeEventListener('error', resizeObserverErrHandler);
};

export default {
  name: 'AppNavigationDrawer',

  data: () => ({
    defaultAvatar: DEFAULT_AVATAR,
    imageURL: "",
    drawer: true,
    dialogLogout: false,
    group: null,
    activeSubMenu: null,
    menu: false,
    notificationDialog: false,
    userDetials: {},
    notification: [],
    notifications: [],
    unreadCount: 0,
    detailsDialog: false,
    selectedNotification: null,
    loading: false,
    loadingMore: false,
    markingAllRead: false,
    notificationPage: 1,
    notificationSize: 10,
    hasMorePages: false,
    profileImageError: false,
    userPermissions: [],

    mainNavModules: [
      { key: "dashboard", title: "Dashboard", dashboard_id: 1, type: "single", icon: "mdi-view-dashboard-outline", to: "/dashboard" },
      {
        key: "lead",
        title: "New Visit Management",
        dashboard_id: 2,
        type: "group",
        icon: "mdi-clipboard-clock-outline",
        children: [
          { key: "add_leads", title: "Add New Visit", dashboard_id: 2, to: "/add-leads" },
          { key: "lead_list", title: "New Visit List", dashboard_id: 2, to: "/leads-list" },
          { key: "leads_approval", title: "New Visit for Approval", dashboard_id: 2, to: "/Leads-For-Approval" },
          { key: "approved_leads", title: "Approved New Visit", dashboard_id: 2, to: "/Approved-Leeds" },
          { key: "rejected_leads", title: "Rejected New Visit", dashboard_id: 2, to: "/Rejected-Leads" },
        ],
      },
      {
        key: "user",
        title: "User Management",
        dashboard_id: 3,
        type: "group",
        icon: "mdi-account-edit-outline",
        children: [
          { key: "user_registration", title: "User Registration", dashboard_id: 3, to: "/user-registration" },
          { key: "user_list", title: "User List", dashboard_id: 3, to: "/users-list" },
          { key: "create_user_role", title: "Create User Role", dashboard_id: 3, to: "/create-user-role" },
          { key: "user_role_list", title: "User Role List", dashboard_id: 3, to: "/user-role-list" },
        ],
      },
      { key: "area", title: "Area Management", dashboard_id: 4, type: "single", icon: "mdi-chart-bell-curve-cumulative", to: "/Create-Area" },
      {
        key: "dealer",
        title: "Dealer Management",
        dashboard_id: 5,
        type: "group",
        icon: "mdi-account-multiple-outline",
        children: [
          { key: "dealer_management", title: "Dealer Registration", dashboard_id: 5, to: "/dealer-registration" },
          { key: "dealer_list", title: "Dealer List", dashboard_id: 5, to: "/dealer-list" },
        ],
      },
      {
        key: "product",
        title: "Product Management",
        dashboard_id: 6,
        type: "group",
        icon: "mdi-package-variant",
        children: [
          { key: "add_product", title: "Add Product", dashboard_id: 6, to: "/add-products" },
          { key: "product_list", title: "Product List", dashboard_id: 6, to: "/products-list" },
          { key: "add_category", title: "Add Category", dashboard_id: 6, to: "/products-category" },
          { key: "category_list", title: "Category List", dashboard_id: 6, to: "/category-list" },
        ],
      },
      {
        key: "route",
        title: "Route Management",
        dashboard_id: 7,
        type: "group",
        icon: "mdi-road-variant",
        children: [
          { key: "create_routes", title: "Create Routes", dashboard_id: 7, to: "/create-routes" },
          { key: "routes_list", title: "Routes List", dashboard_id: 7, to: "/routes-list" },
        ],
      },
      {
        key: "collection",
        title: "Collection Management",
        dashboard_id: 8,
        type: "group",
        icon: "mdi-sale",
        children: [
          { key: "collection_bulk_upload", title: "Collection Bulk Upload", dashboard_id: 8, to: "/Collection-Bulk-Upload" },
          { key: "total_collection", title: "Total Collection", dashboard_id: 8, to: "/total-collections" },
          { key: "overdue_collection", title: "Overdue Collection", dashboard_id: 8, to: "/overdue-collections" },
          { key: "approved_overdues", title: "Approved Overdues", dashboard_id: 8, to: "/approved-overdues" },
          { key: "CollectionNotify", title: "Collection Notify List", dashboard_id: 8, to: "/CollectionNotify" },
        ],
      },
      {
        key: "tracking",
        title: "Tracking Management",
        dashboard_id: 9,
        type: "group",
        icon: "mdi-map-marker-path",
        children: [
          { key: "live_tracking", title: "Live Tracking", dashboard_id: 9, to: "/live-tracking" },
          { key: "tracking_history", title: "Tracking History", dashboard_id: 9, to: "/overall-tracking-history" },
        ],
      },
      {
        key: "bulkUpload",
        title: "BulkUpload Management",
        dashboard_id: 13,
        type: "group",
        icon: "mdi-clipboard-clock-outline",
        required_any_permission_of: [
          "/Create-Area",
          "/dealer-registration",
          "/Collection-Bulk-Upload"
        ],
        children: [
          { key: "Bulk_Upload", title: "Bulk Upload", dashboard_id: 13, to: "/Bulk-Upload" }
        ],
      },
      {
        key: "paymentReversal",
        title: "Payment Reversal",
        dashboard_id: 10,
        type: "group",
        icon: "mdi-cash-refund",
        children: [
          { key: "pending_reversals", title: "Pending Reversals", dashboard_id: 10, to: "/pending-reversals" },
          { key: "approved_reversals", title: "Approved Reversals", dashboard_id: 10, to: "/approved-reversals" },
        ],
      },
      { key: "dailySummary", title: "Daily Summary Report", dashboard_id: 11, type: "single", icon: "mdi-chart-bell-curve-cumulative", to: "/sales-management" },
      { key: "userLogs", title: "User Logs", dashboard_id: 12, type: "single", icon: "mdi-text-box-search-outline", to: "/user-log" },
    ],
  }),

  beforeCreate() {
    this.removeResizeObserverError = suppressResizeObserverErrors();
  },

  mounted() {
    const savedTheme = JSON.parse(localStorage.getItem('theme'));
    if (savedTheme && savedTheme.primaryColor) {
      document.documentElement.style.setProperty('--primary-color', savedTheme.primaryColor);
    }

    this.loadPermissions();
  },

  beforeUnmount() {
    if (this.removeResizeObserverError) {
      this.removeResizeObserverError();
    }
  },

  watch: {
    group() {
      this.drawer = false
    },
    '$route'() {
      this.setActiveMenuFromRoute();
    },
    notificationDialog(isOpen) {
      if (isOpen && this.notifications.length === 0) {
        this.getNotifications(true);
      }
    }
  },

  created() {
    this.imageURL = process.env.VUE_APP_IMAGE_PUBLIC_URL;

    const savedUserDetails = localStorage.getItem("user_details");
    if (savedUserDetails) {
      try {
        this.userDetials = JSON.parse(savedUserDetails);
      } catch (e) {
        console.error("Error parsing user_details from localStorage", e);
      }
    }

    this.getUserDetails();
    this.getUnreadCount();
    setTimeout(() => this.setActiveMenuFromRoute(), 100);
  },

  computed: {
    ...mapState(['organizationSettings']),

    visibleNavModules() {
      const permissions = this.userPermissions;

      // If no permissions, return empty array
      if (!permissions || permissions.length === 0) {
        console.warn('No permissions found, sidebar empty');
        return [];
      }

      console.log('Building sidebar with permissions:', permissions.length);

      return this.mainNavModules
        .map((module) => {
          // Check if module has any permission requirements
          if (module.required_any_permission_of) {
            const hasPermission = module.required_any_permission_of.some(route => this.canReadRoute(route));
            if (!hasPermission) return null;
          }

          // Check if module has any permission in the array
          const hasPermission = permissions.some(p => p.dashboard_id === module.dashboard_id);

          if (!hasPermission) return null;

          if (module.type === "single") {
            const perm = permissions.find(p => p.dashboard_id === module.dashboard_id);
            return perm && perm.read_premission === 1 ? module : null;
          }

          if (module.type === "group" && Array.isArray(module.children)) {
            const children = module.children.filter((child) => {
              if (child.required_permission) {
                return this.canReadRoute(child.required_permission);
              }
              if (child.required_any_permission_of) {
                return child.required_any_permission_of.some(route => this.canReadRoute(route));
              }
              return this.hasChildPermission(child, permissions);
            });

            return children.length ? { ...module, children } : null;
          }

          return null;
        })
        .filter(Boolean);
    },

    sortedNotifications() {
      return [...this.notifications].sort((a, b) => {
        if (a.is_read !== b.is_read) {
          return a.is_read ? 1 : -1;
        }
        return new Date(b.created_at) - new Date(a.created_at);
      });
    }
  },

  methods: {
    // ========== PERMISSION METHODS ==========

    loadPermissions() {
      try {
        console.log('Loading permissions...');

        // ✅ Try to get from 'permissions' key first (this is the plain JSON we now store)
        const permissions = localStorage.getItem('permissions');
        if (permissions) {
          try {
            const parsed = JSON.parse(permissions);
            if (Array.isArray(parsed) && parsed.length > 0) {
              this.userPermissions = parsed;
              console.log('✅ Permissions loaded from permissions key:', this.userPermissions.length);
              return;
            }
          } catch (e) {
            console.warn('Failed to parse permissions');
          }
        }

        // Try to get from loginResponse
        const loginResponse = localStorage.getItem('loginResponse');
        if (loginResponse) {
          try {
            const parsed = JSON.parse(loginResponse);
            if (parsed.accessDashboard && Array.isArray(parsed.accessDashboard) && parsed.accessDashboard.length > 0) {
              this.userPermissions = parsed.accessDashboard;
              console.log('✅ Permissions loaded from loginResponse:', this.userPermissions.length);
              return;
            }
          } catch (e) {
            console.warn('Failed to parse loginResponse');
          }
        }

        // Try to get from userPermissions key
        const userPermissions = localStorage.getItem('userPermissions');
        if (userPermissions) {
          try {
            const parsed = JSON.parse(userPermissions);
            if (Array.isArray(parsed) && parsed.length > 0) {
              this.userPermissions = parsed;
              console.log('✅ Permissions loaded from userPermissions key:', this.userPermissions.length);
              return;
            }
          } catch (e) {
            console.warn('Failed to parse userPermissions');
          }
        }

        // Try to get from user_details
        const userDetails = localStorage.getItem('user_details');
        if (userDetails) {
          try {
            const parsed = JSON.parse(userDetails);
            if (parsed.accessDashboard && Array.isArray(parsed.accessDashboard) && parsed.accessDashboard.length > 0) {
              this.userPermissions = parsed.accessDashboard;
              console.log('✅ Permissions loaded from user_details:', this.userPermissions.length);
              return;
            }
          } catch (e) {
            console.warn('Failed to parse user_details');
          }
        }

        console.warn('⚠️ No permissions found in any storage source');
        this.userPermissions = [];
      } catch (e) {
        console.error('Error loading permissions:', e);
        this.userPermissions = [];
      }
    },

    hasChildPermission(child, permissions) {
      if (!permissions || permissions.length === 0) {
        return false;
      }

      // Find the permission for this child
      const childPermission = permissions.find(p => {
        if (p.dashboard_id !== child.dashboard_id) return false;
        return this.matchPermission(p.module, child.title);
      });

      const hasPermission = childPermission && childPermission.read_premission === 1;
      if (hasPermission) {
        console.log(`✅ Child "${child.title}" has read permission`);
      }
      return hasPermission;
    },

    matchPermission(permissionModule, childTitle) {
      // Direct match (case-insensitive)
      if (permissionModule.toLowerCase() === childTitle.toLowerCase()) {
        return true;
      }

      // Remove spaces and special characters for comparison
      const normalize = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (normalize(permissionModule) === normalize(childTitle)) {
        return true;
      }

      // Special mappings for Collection Notify
      if (permissionModule.toLowerCase() === 'collection notify list' &&
        (childTitle.toLowerCase() === 'collectionnotify' ||
          childTitle.toLowerCase() === 'collection notify' ||
          childTitle.toLowerCase() === 'collection notify list')) {
        return true;
      }

      // Check if child title contains permission module or vice versa
      if (permissionModule.toLowerCase().includes(childTitle.toLowerCase()) ||
        childTitle.toLowerCase().includes(permissionModule.toLowerCase())) {
        return true;
      }

      return false;
    },

    // ========== PROFILE IMAGE METHODS ==========
    getProfileImageUrl() {
      if (this.profileImageError) {
        return this.defaultAvatar;
      }

      if (!this.userDetials || !this.userDetials.user_image) {
        return this.defaultAvatar;
      }

      const imagePath = this.userDetials.user_image;

      if (imagePath.startsWith('data:image') ||
        imagePath.startsWith('blob:') ||
        imagePath.startsWith('http://') ||
        imagePath.startsWith('https://')) {
        return imagePath;
      }

      let cleanPath = imagePath.replace(/^\/?public\//, '/');
      if (!cleanPath.startsWith('/')) {
        cleanPath = '/' + cleanPath;
      }

      const baseUrl = (process.env.VUE_APP_IMAGE_PUBLIC_URL || process.env.VUE_APP_API_BASE_URL || '').replace(/\/+$/, '');
      const fullUrl = `${baseUrl}${cleanPath}`;
      const cacheBuster = `?t=${this.userDetials.updated_at || Date.now()}`;

      return fullUrl + cacheBuster;
    },

    handleProfileImageError() {
      console.warn('Profile image failed to load');
      this.profileImageError = true;
      this.$forceUpdate();
    },

    canReadRoute(routePath) {
      return canAccessRoute(routePath, "read");
    },

    getAuthHeaders() {
      const token = localStorage.getItem('token');
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    },

    getUserId() {
      return localStorage.getItem('userId');
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    },

    formatFullDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    formatKey(key) {
      return key.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },

    formatType(type) {
      if (!type) return '';
      return type.split('_').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },

    capitalize(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    },

    openNotificationDetails(notification) {
      this.selectedNotification = notification;
      this.detailsDialog = true;
      this.notificationDialog = false;
    },

    closeDetailsDialog() {
      this.detailsDialog = false;
      setTimeout(() => {
        this.selectedNotification = null;
      }, 300);
    },

    markAsReadAndClose() {
      if (this.selectedNotification && !this.selectedNotification.is_read) {
        this.markAsRead(this.selectedNotification.id);
      }
      this.closeDetailsDialog();
    },

    navigateToEntity() {
      if (this.selectedNotification && !this.selectedNotification.is_read) {
        this.markAsRead(this.selectedNotification.id);
      }

      if (this.selectedNotification?.entity_type === 'lead' && this.selectedNotification?.entity_id) {
        this.closeDetailsDialog();
        this.$router.push(`/lead-details/${this.selectedNotification.entity_id}`);
      } else if (this.selectedNotification?.entity_type === 'route' && this.selectedNotification?.entity_id) {
        this.closeDetailsDialog();
        this.$router.push(`/route-details/${this.selectedNotification.entity_id}`);
      }
    },

    async getNotifications(reset = false) {
      if (reset) {
        this.loading = true;
        this.notificationPage = 1;
      } else {
        this.loadingMore = true;
      }

      const userId = this.getUserId();

      try {
        const response = await axios.get('/api/notifications', {
          headers: this.getAuthHeaders(),
          params: {
            userId: userId,
            channel: 'web',
            unread_only: false,
            size: this.notificationSize,
            page: this.notificationPage
          }
        });

        if (response.data.success) {
          const paginatedData = response.data.data;
          if (reset) {
            this.notifications = paginatedData.data;
          } else {
            this.notifications = [...this.notifications, ...paginatedData.data];
          }

          this.hasMorePages = paginatedData.current_page < paginatedData.last_page;
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    async getUnreadCount() {
      const userId = this.getUserId();
      try {
        const response = await axios.get('/api/notifications/unread-count', {
          headers: this.getAuthHeaders(),
          params: { userId: userId }
        });

        if (response.data.success) {
          this.unreadCount = response.data.unread_count;
        }
      } catch (error) {
        console.error("Error fetching unread count:", error);
      }
    },

    async markAsRead(notificationId) {
      try {
        const response = await axios.put(`/api/notifications/${notificationId}/read`, {}, {
          headers: this.getAuthHeaders()
        });

        if (response.data.success) {
          const notification = this.notifications.find(n => n.id === notificationId);
          if (notification && !notification.is_read) {
            notification.is_read = true;
            notification.read_at = new Date().toISOString();
            this.unreadCount = Math.max(0, this.unreadCount - 1);
            this.notifications = [...this.notifications];
          }
          if (this.selectedNotification && this.selectedNotification.id === notificationId) {
            this.selectedNotification.is_read = true;
          }
        }
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    },

    async markAllAsRead() {
      this.markingAllRead = true;
      const userId = this.getUserId();

      try {
        const response = await axios.put('/api/notifications/mark-all-read', {
          userId: parseInt(userId)
        }, {
          headers: this.getAuthHeaders()
        });

        if (response.data.success) {
          this.notifications.forEach(notification => {
            if (!notification.is_read) {
              notification.is_read = true;
              notification.read_at = new Date().toISOString();
            }
          });
          this.unreadCount = 0;
          this.notifications = [...this.notifications];
        }
      } catch (error) {
        console.error("Error marking all as read:", error);
      } finally {
        this.markingAllRead = false;
      }
    },

    loadMoreNotifications() {
      if (this.hasMorePages && !this.loadingMore && !this.loading) {
        this.notificationPage++;
        this.getNotifications(false);
      }
    },

    getNotificationIcon(type) {
      const icons = {
        'lead_assigned': 'mdi-account-plus',
        'route_assigned': 'mdi-road',
        'collection_reminder': 'mdi-cash-clock',
        'payment_received': 'mdi-cash-check',
        'lead_approved': 'mdi-check-circle',
        'lead_rejected': 'mdi-close-circle'
      };
      return icons[type] || 'mdi-bell';
    },

    getNotificationColor(type) {
      const colors = {
        'lead_assigned': '#2196F3',
        'route_assigned': '#4CAF50',
        'collection_reminder': '#FF9800',
        'payment_received': '#4CAF50',
        'lead_approved': '#4CAF50',
        'lead_rejected': '#F44336'
      };
      return colors[type] || '#9E9E9E';
    },

    getUserDetails() {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('userId', userId);

      axios.post('/api/getUserDetailsIntoDashboard', formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(response => {
          if (response.status === 200) {
            this.userDetials = response.data.data;
            this.profileImageError = false;
            localStorage.setItem("user_details", JSON.stringify(response.data.data));
          }
        })
        .catch(error => {
          console.error("Error fetching user details:", error);
        });
    },

    getAllNotification() {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');

      const formData = new FormData();
      formData.append('userId', userId);

      axios.post('/api/get/notifications/sale-collection', formData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
        .then(response => {
          if (response.status === 200) {
            this.notification = response.data.data;
          }
        })
        .catch(error => {
          console.error("Error fetching chart details:", error);
        });
    },

    async isLogOut() {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('userId', userId);

      try {
        await axios.post('/api/logout', formData, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        localStorage.clear();
        window.location.href = '/login';
      } catch (error) {
        console.error('Error logging out:', error);
      }
    },

    homeReload() {
      window.location.href = '/dashboard';
    },

    toggleSubMenu(menuKey) {
      this.activeSubMenu = this.activeSubMenu === menuKey ? null : menuKey;
    },

    setActiveMenuFromRoute() {
      const currentPath = this.$route.path;
      this.activeSubMenu = null;
      for (const module of this.visibleNavModules) {
        if (module.type === 'group' && module.children) {
          const hasChild = module.children.some(child => child.to === currentPath);
          if (hasChild) {
            this.activeSubMenu = module.key;
            break;
          }
        }
      }
    },
  },
}
</script>

<style scoped>
@import "../styles/navigation_drawer/main_navigation_drawer.scss";

.notification-popup {
  border-radius: 8px !important;
  overflow: hidden !important;
  will-change: transform, opacity;
}

.notification-header {
  background: #ffffff;
}

.notification-list {
  background: #ffffff;
}

.notification-item-wrapper {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.notification-item-wrapper:hover {
  background-color: #f8f9fa;
}

.unread-item-wrapper {
  background-color: #fef5f5;
}

.unread-item-wrapper:hover {
  background-color: #fef0f0;
}

.notification-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.notification-title {
  line-height: 1.4;
}

.unread-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #BB0000;
  vertical-align: middle;
}

.notification-body {
  line-height: 1.3;
  white-space: normal;
  word-break: break-word;
}

.notification-footer {
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
}

.notification-details-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.dialog-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.details-content {
  max-height: 500px;
  overflow-y: auto;
}

.detail-section {
  padding: 4px 0;
}

.detail-label {
  letter-spacing: 0.5px;
  font-weight: 600;
}

.details-grid {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 4px 0;
}

.detail-key {
  font-weight: 500;
  min-width: 120px;
}

.detail-value {
  color: #333;
  word-break: break-word;
  flex: 1;
  text-align: right;
}

.v-menu .v-overlay__content {
  will-change: transform;
}

.notification-popup *,
.notification-details-dialog * {
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

.details-content::-webkit-scrollbar,
.notification-list::-webkit-scrollbar {
  width: 6px;
}

.details-content::-webkit-scrollbar-track,
.notification-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.details-content::-webkit-scrollbar-thumb,
.notification-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.details-content::-webkit-scrollbar-thumb:hover,
.notification-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.avatar-image {
  border-radius: 50%;
  object-fit: cover;
}
</style>